import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Button, Card, Form, Icon, Modal, TextLink } from '@upside/flux-ui';

import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { clsx } from 'clsx';
import { AppContext } from 'context/App';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { InputOTP, InputOTPSlot } from './InputOTP';
import { TIMEOUT_MINUTES } from './const';

const defaultCode = '';

export function MultiFactor({
  handleCancel,
  handleTimeout,
  mfaState,
}: {
  handleCancel: () => void;
  handleTimeout: () => void;
  mfaState?: {
    factorId: string;
    mfaChallengeId: string;
    maskedIdentifier: string;
  };
}) {
  const context = useContext(AppContext);

  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    // 3 minutes
    const timeout = TIMEOUT_MINUTES * 60 * 1000;

    // Redirect to login page after timeout
    const timer = setTimeout(() => {
      handleTimeout();
    }, timeout);
    return () => clearTimeout(timer);
  }, [handleTimeout]);

  const { factorId, mfaChallengeId, maskedIdentifier } = mfaState || {
    factorId: '',
    mfaChallengeId: '',
    maskedIdentifier: '',
  };

  const { t: translate } = useTranslation('mfa');

  const formSchema = z.object({
    code: z.string().min(6),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: defaultCode,
    },
  });

  const { setValue, setError, formState, handleSubmit } = form;

  const { isSubmitting, isValid, isValidating, isSubmitSuccessful } = formState;

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = useCallback(
    async ({ code }) => {
      try {
        const result =
          context &&
          context.authentication.verifyMfaChallenge &&
          (await context.authentication.verifyMfaChallenge({
            otp: code,
            factorId,
            mfaChallengeId,
          }));

        if (result?.status !== 200) {
          setValue('code', defaultCode);
          setError('code', {
            message: translate('error'),
          });
        }
      } catch {
        setValue('code', defaultCode);
        setError('code', {
          message: translate('error'),
        });
      }
    },
    [context, factorId, mfaChallengeId, setError, setValue, translate],
  );

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (isValid && !isValidating && !isSubmitting && !isSubmitSuccessful) {
      formRef.current?.requestSubmit();
    }
  }, [isValid, isValidating, isSubmitting, isSubmitSuccessful]);
  return (
    <div className="my-auto flex w-full flex-col items-center gap-8">
      <Card
        className={clsx(
          'relative flex w-full max-w-[400px] grow flex-col items-center gap-6 rounded-xl p-8 shadow-md',
          isSubmitting && 'h-96',
        )}
      >
        {isSubmitting && (
          <Icon
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-active-foreground"
            name="spinner"
            spin
            icon={faSpinnerThird}
            size="2xl"
          />
        )}
        {!isSubmitting && (
          <>
            <h1 className="text-center text-2xl font-medium">
              {translate('title')}
            </h1>
            <Form {...form}>
              <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                <Form.Field
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <Form.Item className="space-y-6">
                      <Form.Label>
                        <h3 className="text-center">
                          {translate('enterCode')}
                          <br />
                          +xx xxxx xxx{maskedIdentifier}
                        </h3>
                      </Form.Label>
                      <Form.Control>
                        <InputOTP autoFocus maxLength={6} {...field}>
                          {new Array(6).fill(0).map((_, index) => (
                            <InputOTPSlot
                              // eslint-disable-next-line react/no-array-index-key
                              key={`otp-field-${index}`}
                              index={index}
                            />
                          ))}
                        </InputOTP>
                      </Form.Control>
                      <Form.Message />
                    </Form.Item>
                  )}
                />
              </form>
            </Form>
            <span className="text-center text-sm text-foreground-secondary">
              {translate('expiresIn', { minutes: TIMEOUT_MINUTES })}
            </span>
            <TextLink onClick={() => setModalOpen(true)}>
              <TextLink.Label>{translate('troubleCode')}</TextLink.Label>
            </TextLink>
          </>
        )}
      </Card>
      <TextLink onClick={handleCancel} type="button" className="z-0">
        <TextLink.Label>{translate('cancel')}</TextLink.Label>
      </TextLink>
      <Modal open={modalOpen}>
        <Modal.Overlay />
        <Modal.Content>
          <Card className="flex max-w-[400px] flex-col items-center gap-6 p-10">
            <div className="grid gap-4">
              <h2 className="text-center text-2xl font-medium">
                {translate('codeNotReceived')}
              </h2>
              <p className="text-center">{translate('requestNewCode')}</p>
            </div>
            <Button className="px-8" size="lg" onClick={handleCancel}>
              {translate('goToLogin')}
            </Button>
          </Card>
        </Modal.Content>
      </Modal>
    </div>
  );
}
